import { useState, useRef, useEffect, FormEvent, KeyboardEvent, ClipboardEvent } from 'react';
import { useNotification } from '../shared/NotificationModal';
import Button from '../shared/Button';
import { TEXT } from '../shared/Typography';

const API_BASE = import.meta.env.VITE_API_BASE || '';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

interface MfaVerificationProps {
  mfaToken: string;
  maskedEmail: string;
  onVerified: (data: { token: string; user: any }) => void;
  onCancel: () => void;
}

export default function MfaVerification({
  mfaToken,
  maskedEmail,
  onVerified,
  onCancel,
}: MfaVerificationProps) {
  const notification = useNotification();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [busy, setBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const setDigit = (index: number, value: string) => {
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    return next;
  };

  const handleChange = (index: number, value: string) => {
    // Allow only single digit
    const char = value.replace(/\D/g, '').slice(-1);
    const next = setDigit(index, char);

    // Auto-advance to next input
    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    if (char && next.every((d) => d !== '')) {
      submitCode(next.join(''));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Move focus back on backspace when current input is empty
      setDigit(index - 1, '');
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    const next = [...digits];
    for (let i = 0; i < CODE_LENGTH; i++) {
      next[i] = pasted[i] || '';
    }
    setDigits(next);

    // Focus the last filled input or the next empty one
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if full code pasted
    if (next.every((d) => d !== '')) {
      submitCode(next.join(''));
    }
  };

  const submitCode = async (code: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/auth/verify-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, code }),
      });
      const data = await res.json();

      if (!res.ok) {
        notification.showError(data?.error || 'Verification failed', 'Invalid Code');
        // Clear inputs on failure so user can retry
        setDigits(Array(CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        setBusy(false);
        return;
      }

      onVerified(data);
    } catch (_err) {
      notification.showError('An unexpected error occurred. Please try again.', 'Error');
      setBusy(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== CODE_LENGTH) {
      notification.showError('Please enter the full 6-digit code.', 'Incomplete Code');
      return;
    }
    submitCode(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || busy) return;
    try {
      const res = await fetch(`${API_BASE}/auth/resend-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken }),
      });
      const data = await res.json();

      if (!res.ok) {
        notification.showError(data?.error || 'Failed to resend code', 'Resend Failed');
        return;
      }

      notification.showSuccess('A new code has been sent to your email.', 'Code Sent');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (_err) {
      notification.showError('Failed to resend code. Please try again.', 'Error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4 sm:p-6 md:p-8">
      <div className="bg-white shadow-md rounded-lg w-full max-w-sm sm:max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-blue-50 px-6 sm:px-10 pt-8 sm:pt-10 pb-6 sm:pb-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className={`sm:text-lg ${TEXT.cardTitle}`}>Check your email</h2>
            <p className={`mt-1 ${TEXT.bodyMuted}`}>
              We sent a verification code to
            </p>
            <p className={`mt-0.5 ${TEXT.bodyStrong}`}>{maskedEmail}</p>
          </div>
        </div>

        {/* Code Input */}
        <form onSubmit={handleSubmit} className="px-6 sm:px-10 pt-6 pb-4">
          <div className="flex justify-center gap-2 sm:gap-3 mb-6">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                className="w-11 h-14 sm:w-12 sm:h-16 text-center text-xl sm:text-2xl font-bold border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                disabled={busy}
              />
            ))}
          </div>

          <Button variant="primary" size="lg" type="submit" disabled={busy || digits.some((d) => !d)} fullWidth>
            {busy ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify Code'
            )}
          </Button>

          {/* Resend / Back */}
          <div className="flex items-center justify-between mt-5 mb-2">
            <Button variant="linkMuted" size="sm" onClick={onCancel}>
              Back to login
            </Button>
            <Button variant="link" size="sm" onClick={handleResend} disabled={resendCooldown > 0 || busy}>
              {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 sm:px-10 py-4 sm:py-5 bg-gray-50 border-t border-gray-100">
          <p className={`text-center ${TEXT.caption}`}>
            Code expires in 10 minutes. Check your spam folder if you don&apos;t see the email.
          </p>
        </div>
      </div>
    </div>
  );
}
